import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { FaArrowLeft, FaHotel } from 'react-icons/fa';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  FaBed,
  FaUser,
  FaPhone,
  FaCalendarAlt,
  FaReceipt,
  FaEye,
  FaCheck,
  FaTimes,
  FaArrowRight,
  FaShieldAlt,
  FaTag,
  FaCreditCard,
  FaMoneyBillWave,
  FaUtensils,
} from 'react-icons/fa';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

const RoomUpgrade = () => {
  // Get reservation from session storage
  const reservation = JSON.parse(sessionStorage.getItem('selectedReservation') || '{}');

  const [formData, setFormData] = useState({
    fullName: `${reservation?.firstname || ''} ${reservation?.lastname || ''}`.trim(),
    bookingId: reservation?.bookingid || '',
    phone: reservation?.phonenumber || '',
    totalGuests: reservation?.numberofguests || '',
    currentRoomType: reservation?.roomtypename || '',
    newRoomType: '',
    checkInDate: reservation?.checkindate ? new Date(reservation.checkindate) : new Date(),
    checkOutDate: reservation?.checkoutdate ? new Date(reservation.checkoutdate) : null,
    newCheckInDate: null,
    newCheckOutDate: null,
  });

  const [roomTypes, setRoomTypes] = useState([]);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch room types from admin-roomtypes API
  useEffect(() => {
    const fetchRoomTypes = async () => {
      try {
        const response = await fetch(`${BQ_BASE_URL}/bq/api/admin-roomtypes/`, {
          method: 'GET',
          headers: { accept: 'application/json' },
        });
        const data = await response.json();
        // Extract roomtypes array from the response
        setRoomTypes(data.roomtypes || []);
      } catch (error) {
        console.error('Error fetching room types:', error);
        toast.error('Failed to load room types');
      }
    };
    fetchRoomTypes();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Format date to YYYY-MM-DD without timezone issues
  const formatDateToYYYYMMDD = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handlePreview = async () => {
    // Check if any changes are made
    const hasRoomChange = formData.newRoomType && formData.newRoomType !== formData.currentRoomType;
    const hasCheckInChange =
      formData.newCheckInDate &&
      formatDateToYYYYMMDD(formData.newCheckInDate) !== formatDateToYYYYMMDD(formData.checkInDate);
    const hasCheckOutChange =
      formData.newCheckOutDate &&
      formatDateToYYYYMMDD(formData.newCheckOutDate) !==
        formatDateToYYYYMMDD(formData.checkOutDate);

    if (!hasRoomChange && !hasCheckInChange && !hasCheckOutChange) {
      toast.error('Please make at least one change (room type, check-in date, or check-out date)');
      return;
    }

    setIsLoading(true);
    try {
      const requestBody = {
        new_checkin_date: hasCheckInChange
          ? formatDateToYYYYMMDD(formData.newCheckInDate)
          : formatDateToYYYYMMDD(formData.checkInDate),
        new_checkout_date: hasCheckOutChange
          ? formatDateToYYYYMMDD(formData.newCheckOutDate)
          : formatDateToYYYYMMDD(formData.checkOutDate),
        amenities: [],
        room_services: [],
        food_orders: [],
      };

      // ALWAYS send the room type - either new one if changed, or current one if not changed
      requestBody.new_room_type_name = hasRoomChange
        ? formData.newRoomType
        : formData.currentRoomType;

      const response = await fetch(`${BQ_BASE_URL}/bq/api/modify/${formData.bookingId}/preview`, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const responseData = await response.json();

      if (response.ok) {
        setPreviewData(responseData.data);
        setIsPreviewModalOpen(true);
      } else {
        toast.error(responseData.detail || responseData.message || 'Failed to fetch preview');
      }
    } catch (error) {
      console.error('Preview error:', error);
      toast.error('Network error while fetching preview data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = async () => {
    setIsConfirmationModalOpen(false);
    setIsLoading(true);

    try {
      const params = new URLSearchParams();

      params.append(
        'new_checkin_date',
        formData.newCheckInDate
          ? formatDateToYYYYMMDD(formData.newCheckInDate)
          : formatDateToYYYYMMDD(formData.checkInDate)
      );
      params.append(
        'new_checkout_date',
        formData.newCheckOutDate
          ? formatDateToYYYYMMDD(formData.newCheckOutDate)
          : formatDateToYYYYMMDD(formData.checkOutDate)
      );

      // ALWAYS send the room type - either new one if changed, or current one if not changed
      params.append(
        'new_room_type_name',
        formData.newRoomType && formData.newRoomType !== formData.currentRoomType
          ? formData.newRoomType
          : formData.currentRoomType
      );

      const url = `${BQ_BASE_URL}/bq/api/modify/${formData.bookingId}/modify?${params.toString()}`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          accept: 'application/json',
        },
      });

      const data = await response.json();

      if (response.ok) {
        // Clear the selected reservation from session storage
        sessionStorage.removeItem('selectedReservation');

        // Show success toast with the message from API
        toast.success(data.message || 'Reservation modified successfully', {
          onClose: () => {
            // Navigate back to front desk operations after toast closes
            window.location.href =
              '/dashboard/core-modules/property-management-system/front-desk-operations';
          },
        });

        // Reset form
        setFormData((prev) => ({
          ...prev,
          newRoomType: '',
          newCheckInDate: null,
          newCheckOutDate: null,
        }));
      } else {
        toast.error(data.detail || data.message || 'Failed to modify reservation');
      }
    } catch (error) {
      console.error('Modification error:', error);
      toast.error('An error occurred while modifying the reservation');
    } finally {
      setIsLoading(false);
    }
  };
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '₹0.00';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatDisplayDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      // Handle YYYY-MM-DD format directly
      if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = dateString.split('-');
        const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
      }
      return new Date(dateString).toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  // Don't render if no booking selected
  if (!formData.bookingId) {
    return (
      <div className="p-6 bg-white min-h-screen">
        <div className="max-w-6xl mx-auto mt-8 p-8 bg-yellow-50 rounded-lg text-center">
          <p className="text-yellow-800 font-semibold">
            No reservation selected. Please select a reservation from the room management page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-white min-h-screen">
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      <NavLink
        to="/dashboard/core-modules/property-management-system/front-desk-operations"
        className="flex items-center text-gray-400 hover:text-black mb-4"
      >
        <FaArrowLeft className="mr-3 text-md" />
        <span className="text-lg">Back to Front Desk Operations</span>
      </NavLink>

      {/* Header Section */}
      <header className="text-center mb-8 mt-6">
        <div className="flex items-center justify-center space-x-3 mb-3">
          <div className="p-2 bg-gray-100 rounded-lg">
            <FaBed className="text-2xl text-gray-700" />
          </div>
          <h1 className="text-3xl font-semibold text-gray-900">Modify Reservation</h1>
        </div>
        <p className="text-gray-600 max-w-2xl mx-auto leading-relaxed">
          Update room type, check-in date, or check-out date. Preview all changes before confirming.
        </p>
        <div className="w-24 h-1 bg-gray-300 mx-auto mt-4 rounded-full"></div>
      </header>

      {/* Main Form Section */}
      <div className="max-w-6xl mx-auto bg-gray-50 rounded-xl shadow-sm border border-gray-200 p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Guest Information */}
          <div className="flex flex-col">
            <label className="text-sm font-semibold text-gray-700 mb-2 flex items-center space-x-2">
              <FaUser className="text-gray-500" />
              <span>Guest Name</span>
            </label>
            <input
              value={formData.fullName}
              readOnly
              className="w-full p-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
            />
          </div>

          {/* Reservation Number */}
          <div className="flex flex-col">
            <label className="text-sm font-semibold text-gray-700 mb-2">Reservation Number</label>
            <input
              value={formData.bookingId}
              readOnly
              className="w-full p-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
            />
          </div>

          {/* Phone Number */}
          <div className="flex flex-col">
            <label className="text-sm font-semibold text-gray-700 mb-2 flex items-center space-x-2">
              <FaPhone className="text-gray-500" />
              <span>Phone Number</span>
            </label>
            <input
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800 bg-white"
            />
          </div>

          {/* Number of Guests */}
          <div className="flex flex-col">
            <label className="text-sm font-semibold text-gray-700 mb-2">Number of Guests</label>
            <input
              name="totalGuests"
              type="number"
              value={formData.totalGuests}
              onChange={handleChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800 bg-white"
            />
          </div>

          {/* Current Room Type */}
          <div className="flex flex-col">
            <label className="text-sm font-semibold text-gray-700 mb-2 flex items-center space-x-2">
              <FaBed className="text-gray-500" />
              <span>Current Room Type</span>
            </label>
            <input
              value={formData.currentRoomType}
              readOnly
              className="w-full p-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
            />
          </div>

          {/* New Room Type - Optional */}
          <div className="flex flex-col">
            <label className="text-sm font-semibold text-gray-700 mb-2">
              New Room Type <span className="text-gray-400 text-xs">(Optional)</span>
            </label>
            <select
              name="newRoomType"
              value={formData.newRoomType}
              onChange={handleChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800 bg-white"
            >
              <option value="">Same Room Type</option>
              {roomTypes
                .filter((rt) => rt.roomtypename !== formData.currentRoomType)
                .map((roomType) => (
                  <option key={roomType.roomtypeid} value={roomType.roomtypename}>
                    {roomType.roomtypename}
                  </option>
                ))}
            </select>
          </div>

          {/* Current Check-In Date */}
          <div className="flex flex-col">
            <label className="text-sm font-semibold text-gray-700 mb-2">
              Current Check-In Date
            </label>
            <input
              type="text"
              value={formatDisplayDate(formatDateToYYYYMMDD(formData.checkInDate))}
              readOnly
              className="w-full p-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
            />
          </div>

          {/* New Check-In Date - Optional */}
          <div className="flex flex-col">
            <label className="text-sm font-semibold text-gray-700 mb-2">
              New Check-In Date <span className="text-gray-400 text-xs">(Optional)</span>
            </label>
            <DatePicker
              selected={formData.newCheckInDate}
              onChange={(date) => setFormData({ ...formData, newCheckInDate: date })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800 bg-white"
              placeholderText="Select new check-in date"
              dateFormat="MMM dd, yyyy"
              minDate={new Date()}
              maxDate={formData.newCheckOutDate || formData.checkOutDate}
              popperPlacement="bottom-start"
              isClearable
              showPopperArrow={false}
              calendarClassName="custom-calendar"
            />
          </div>

          {/* Current Check-Out Date */}
          <div className="flex flex-col">
            <label className="text-sm font-semibold text-gray-700 mb-2">
              Current Check-Out Date
            </label>
            <input
              type="text"
              value={formatDisplayDate(formatDateToYYYYMMDD(formData.checkOutDate))}
              readOnly
              className="w-full p-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
            />
          </div>

          {/* New Check-Out Date - Optional */}
          <div className="flex flex-col">
            <label className="text-sm font-semibold text-gray-700 mb-2">
              New Check-Out Date <span className="text-gray-400 text-xs">(Optional)</span>
            </label>
            <DatePicker
              selected={formData.newCheckOutDate}
              onChange={(date) => setFormData({ ...formData, newCheckOutDate: date })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800 bg-white"
              placeholderText="Select new check-out date"
              dateFormat="MMM dd, yyyy"
              minDate={formData.newCheckInDate || formData.checkInDate}
              popperPlacement="bottom-start"
              isClearable
              showPopperArrow={false}
              calendarClassName="custom-calendar"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row justify-end gap-4 mt-8 pt-6 border-t border-gray-200">
          <button
            onClick={handlePreview}
            disabled={isLoading}
            className="flex items-center justify-center space-x-2 px-6 py-3 border border-gray-800 text-gray-800 font-semibold rounded-lg hover:bg-gray-100 transition-all duration-200 min-w-[140px] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FaEye />
            <span>{isLoading ? 'Loading...' : 'Preview Changes'}</span>
          </button>
          <button
            onClick={() => setIsConfirmationModalOpen(true)}
            disabled={
              isLoading ||
              (!formData.newRoomType && !formData.newCheckInDate && !formData.newCheckOutDate)
            }
            className="flex items-center justify-center space-x-2 px-6 py-3 bg-gray-900 text-white font-semibold rounded-lg hover:bg-gray-800 transition-all duration-200 min-w-[140px] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FaCheck />
            <span>Update Reservation</span>
          </button>
        </div>
      </div>

      {/* Confirmation Modal */}
      {isConfirmationModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-8 rounded-xl shadow-2xl max-w-md w-full">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-gray-100 rounded-lg">
                <FaBed className="text-xl text-gray-700" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Confirm Changes</h2>
            </div>

            <p className="text-gray-600 mb-4">Please review the following changes:</p>

            <div className="bg-gray-50 p-4 rounded-lg mb-6 space-y-3">
              {formData.newRoomType && formData.newRoomType !== formData.currentRoomType && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Room Type:</span>
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-gray-900">{formData.currentRoomType}</span>
                    <FaArrowRight className="text-gray-400 text-sm" />
                    <span className="font-semibold text-gray-900">{formData.newRoomType}</span>
                  </div>
                </div>
              )}

              {formData.newCheckInDate && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Check-in:</span>
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-gray-900">
                      {formatDisplayDate(formatDateToYYYYMMDD(formData.checkInDate))}
                    </span>
                    <FaArrowRight className="text-gray-400 text-sm" />
                    <span className="font-semibold text-gray-900">
                      {formatDisplayDate(formatDateToYYYYMMDD(formData.newCheckInDate))}
                    </span>
                  </div>
                </div>
              )}

              {formData.newCheckOutDate && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Check-out:</span>
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-gray-900">
                      {formatDisplayDate(formatDateToYYYYMMDD(formData.checkOutDate))}
                    </span>
                    <FaArrowRight className="text-gray-400 text-sm" />
                    <span className="font-semibold text-gray-900">
                      {formatDisplayDate(formatDateToYYYYMMDD(formData.newCheckOutDate))}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setIsConfirmationModalOpen(false)}
                className="flex items-center space-x-2 px-4 py-2.5 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-all duration-200"
              >
                <FaTimes />
                <span>Cancel</span>
              </button>
              <button
                onClick={handleConfirm}
                disabled={isLoading}
                className="flex items-center space-x-2 px-4 py-2.5 bg-gray-900 text-white font-semibold rounded-lg hover:bg-gray-800 transition-all duration-200 disabled:opacity-50"
              >
                <FaCheck />
                <span>{isLoading ? 'Processing...' : 'Confirm Update'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal - Only Amounts, No Policy Rules */}
      {isPreviewModalOpen && previewData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full flex flex-col max-h-[90vh]">
            {/* Header - Fixed */}
            <div className="p-8 pb-4 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    <FaReceipt className="text-xl text-gray-700" />
                  </div>
                  <h2 className="text-2xl font-semibold text-gray-900">Preview Changes</h2>
                </div>
                {previewData.room_preview?.modification_type && (
                  <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium flex items-center space-x-1">
                    <FaTag className="text-xs" />
                    <span>{previewData.room_preview.modification_type.toUpperCase()}</span>
                  </span>
                )}
              </div>
            </div>

            {/* Content - Scrollable */}
            <div className="p-8 pt-4 overflow-y-auto flex-grow">
              <div className="space-y-6">
                {/* Booking Overview */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-4">
                  <div className="space-y-2">
                    <div>
                      <p className="text-sm font-semibold text-gray-600">Reservation Number</p>
                      <p className="text-md font-semibold text-gray-900">
                        {previewData.booking_id}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm font-semibold text-gray-600">Stay Dates</p>
                      <p className="text-md font-semibold text-gray-900">
                        {formatDisplayDate(previewData.room_preview?.new_checkin)} -{' '}
                        {formatDisplayDate(previewData.room_preview?.new_checkout)}
                      </p>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-600">Room Type</p>
                    <p className="text-md font-semibold text-gray-900">
                      {previewData.room_preview?.old_room_type}{' '}
                      {previewData.room_preview?.new_room_type &&
                      previewData.room_preview?.new_room_type !==
                        previewData.room_preview?.old_room_type ? (
                        <>
                          <span className="text-black">
                            {previewData.room_preview?.new_room_type}
                          </span>
                        </>
                      ) : (
                        <span className="text-gray-500 ml-2">(No change)</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800">Number of nights</p>
                    <p className="text-md text-gray-900 mt-1">
                      {previewData.room_preview?.total_nights} nights stay
                    </p>
                  </div>
                </div>

                {/* Cost Breakdown */}
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Cost Breakdown</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center py-1">
                      <span className="text-gray-600">Original Room Total</span>
                      <span className="font-semibold text-gray-900">
                        {formatCurrency(previewData.room_preview?.old_total)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-1">
                      <span className="text-gray-600">New Room Total</span>
                      <span className="font-semibold text-gray-900">
                        {formatCurrency(previewData.room_preview?.new_total)}
                      </span>
                    </div>

                    {previewData.fixed_enhance_services?.total > 0 && (
                      <div className="flex justify-between items-center py-2">
                        <span className="text-gray-600">Enhance Services</span>
                        <span className="font-semibold text-gray-900">
                          {formatCurrency(previewData.fixed_enhance_services.total)}
                        </span>
                      </div>
                    )}

                    <div className="flex justify-between items-center py-1 border-t border-gray-200 pt-3">
                      <span className="text-gray-900 font-semibold">Price Difference</span>
                      <span
                        className={`font-bold text-lg ${
                          previewData.room_preview?.price_difference > 0
                            ? 'text-gray-900'
                            : previewData.room_preview?.price_difference < 0
                              ? 'text-gray-900'
                              : 'text-gray-900'
                        }`}
                      >
                        {previewData.room_preview?.price_difference > 0 ? '+' : ''}
                        {formatCurrency(previewData.room_preview?.price_difference)}
                      </span>
                    </div>

                    {previewData.room_preview?.price_details?.service_fee > 0 && (
                      <div className="flex justify-between items-center py-1">
                        <span className="text-gray-900 font-semibold">
                          Current Modification Service Fee
                        </span>
                        <span className="font-semibold text-gray-900">
                          {formatCurrency(previewData.room_preview.price_details.service_fee)}
                        </span>
                      </div>
                    )}
                    {previewData.combined?.service_fee_total_all_modifications > 0 && (
                      <div className="flex justify-between items-center py-1">
                        <span className="text-gray-900 font-semibold">Total Service Fee</span>
                        <span className="font-semibold text-gray-900">
                          {formatCurrency(previewData.combined.service_fee_total_all_modifications)}
                        </span>
                      </div>
                    )}
                    {/* Net Financial Impact */}
                    <div className="flex justify-between items-center ">
                      <span className="font-semibold text-gray-900">Total Additional Amount</span>
                      <span
                        className={`font-bold text-xl ${
                          previewData.combined?.net_financial_impact > 0
                            ? 'text-green-600'
                            : previewData.combined?.net_financial_impact < 0
                              ? 'text-red-600'
                              : 'text-gray-900'
                        }`}
                      >
                        {previewData.combined?.net_financial_impact > 0 ? '+' : ''}
                        {formatCurrency(previewData.combined?.net_financial_impact)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Existing Services - Only show if there are items */}
                {previewData.service_preview?.existing_items?.length > 0 && (
                  <div className="border-t border-gray-200 pt-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Current Additional Services
                    </h3>
                    <div className="space-y-3">
                      {previewData.service_preview.existing_items.map((item, index) => (
                        <div
                          key={index}
                          className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0"
                        >
                          <div className="flex items-start space-x-3">
                            <div className="p-1.5 bg-gray-100 rounded-lg">
                              {item.type === 'amenity' ? (
                                <FaHotel className="text-gray-600" />
                              ) : item.type === 'food' ? (
                                <FaUtensils className="text-gray-600" />
                              ) : (
                                <FaReceipt className="text-gray-600" />
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{item.name || item.title}</p>
                              <div className="flex items-center space-x-2 mt-1">
                                <span className="text-xs px-2 py-1 bg-gray-100 rounded-full text-gray-600">
                                  Qty: {item.quantity}
                                </span>
                                {!item.is_refundable && (
                                  <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full flex items-center space-x-1">
                                    <FaShieldAlt className="text-xs" />
                                    <span>Non-refundable</span>
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-gray-900">
                              {formatCurrency(item.total_amount)}
                            </p>
                            <p className="text-xs text-gray-500">
                              Tax: {formatCurrency(item.tax_amount)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Payment Summary */}
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Summary</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center rounded-lg">
                      <span className="font-semibold text-gray-800">Total amount</span>
                      <span
                        className={`font-bold text-lg ${
                          previewData.combined?.projected_total_after_preview > 0
                            ? 'text-red-600'
                            : 'text-green-600'
                        }`}
                      >
                        {formatCurrency(previewData.combined?.projected_total_after_preview)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-1">
                      <span className="text-gray-800">Amount Paid</span>
                      <span className="font-semibold text-green-600">
                        {formatCurrency(previewData.combined?.amount_paid)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-1">
                      <span className="text-gray-800">Amount Due</span>
                      <span className="font-semibold text-gray-900">
                        {formatCurrency(previewData.combined?.amount_due_after_preview)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-1">
                      <span className="text-gray-800">Refund Amount</span>
                      <span className="font-semibold text-gray-900">
                        {formatCurrency(previewData.combined?.total_refund)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer - Fixed */}
            <div className="p-8 pt-4 border-t border-gray-200 flex-shrink-0">
              <div className="flex justify-end">
                <button
                  onClick={() => setIsPreviewModalOpen(false)}
                  className="px-6 py-2.5 bg-gray-900 text-white font-semibold rounded-lg hover:bg-gray-800 transition-all duration-200"
                >
                  Close Preview
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Calendar Styles */}
      <style jsx>{`
        .custom-calendar {
          font-family: inherit;
          border-radius: 0.5rem;
          box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
        }
        .react-datepicker {
          border: 1px solid #e5e7eb;
          border-radius: 0.5rem;
        }
        .react-datepicker__header {
          background-color: #f9fafb;
          border-bottom: 1px solid #e5e7eb;
          border-top-left-radius: 0.5rem;
          border-top-right-radius: 0.5rem;
          padding-top: 0.75rem;
        }
        .react-datepicker__day--selected {
          background-color: #1f2937 !important;
          color: white !important;
          border-radius: 0.375rem;
        }
        .react-datepicker__day--keyboard-selected {
          background-color: #374151 !important;
          color: white !important;
          border-radius: 0.375rem;
        }
        .react-datepicker__day:hover {
          background-color: #f3f4f6 !important;
          border-radius: 0.375rem;
        }
      `}</style>
    </div>
  );
};

export default RoomUpgrade;
